import { useState, useEffect, useRef } from 'react';
import { gql } from '@/lib/gql';
import { useKitchen } from '@/lib/kitchen-context';
import { listBlueskyRecipes, blueskyToRecipe, type ParsedRecipe, type BlueskyRecipeRecord } from '@pantry-host/shared/bluesky';
import ImportGrid, { captureActiveElement } from '@pantry-host/shared/components/ImportGrid';
import PixabayImage from '@pantry-host/shared/components/PixabayImage';
import { isBrowser, isServer } from '@pantry-host/shared/env';

const FEED_API = 'https://feed.pantryhost.app/api/handles';
const FEED_RECIPES_API = 'https://feed.pantryhost.app/api/recipes';
const PAGE_SIZE = 50;

const SEED_HANDLES = [
  'joshhuckabee.com', 'recipe.exchange', 'pixeline.be',
  'stephenhunter.xyz', 'bmann.ca', 'rdur.dev',
];

interface FeedRecipe {
  atUri: string;
  recipe: ParsedRecipe;
  handle: string;
}

const CREATE_RECIPE = `mutation(
  $title: String!, $instructions: String!, $ingredients: [RecipeIngredientInput!]!,
  $description: String, $servings: Int, $prepTime: Int, $cookTime: Int,
  $tags: [String!], $photoUrl: String, $sourceUrl: String, $kitchenSlug: String
) {
  createRecipe(
    title: $title, instructions: $instructions, ingredients: $ingredients,
    description: $description, servings: $servings, prepTime: $prepTime, cookTime: $cookTime,
    tags: $tags, photoUrl: $photoUrl, sourceUrl: $sourceUrl, kitchenSlug: $kitchenSlug
  ) { id slug }
}`;

/**
 * The reusable Bluesky feed browse experience — search, tag filters, dual-mode
 * (Browse/Bulk), cursor pagination. Rendered both standalone at
 * `/kitchens/{kitchen}/recipes/feeds/bluesky` (via BlueskyFeedsPage, which adds
 * the page chrome) and embedded as the first tab of the Import page.
 */
export default function BlueskyFeedBrowse() {
  const kitchen = useKitchen();
  const recipesBase = `/kitchens/${kitchen}/recipes`;
  const [recipes, setRecipes] = useState<FeedRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [pixabayKey, setPixabayKey] = useState<string | null>(null);
  const [pixabayEnabled, setPixabayEnabled] = useState(false);
  const pixabayActive = pixabayEnabled && !!pixabayKey;
  useEffect(() => {
    fetch('/api/settings-read?reveal=PIXABAY_API_KEY')
      .then((r) => r.ok ? r.json() : null)
      .then((j: { values?: Record<string, string | null> } | null) => {
        if (j?.values?.PIXABAY_API_KEY) setPixabayKey(j.values.PIXABAY_API_KEY);
        if (j?.values?.PIXABAY_FALLBACK_ENABLED === 'true') setPixabayEnabled(true);
      })
      .catch(() => {});
  }, []);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const [mode, setMode] = useState<'bulk' | 'browse'>(() => {
    if (isServer) return 'browse';
    return (localStorage.getItem('bsky-feeds-mode') as 'bulk' | 'browse') || 'browse';
  });
  useEffect(() => {
    if (isBrowser) localStorage.setItem('bsky-feeds-mode', mode);
  }, [mode]);

  // Server-side feed (preferred) → falls back to per-handle XRPC fan-out.
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [serverFeedAvailable, setServerFeedAvailable] = useState(true);

  // Focus + screen-reader handling for Load more.
  const focusIdxRef = useRef<number | null>(null);
  const loadMoreBtnRef = useRef<HTMLButtonElement | null>(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${FEED_RECIPES_API}?limit=${PAGE_SIZE}`);
        if (res.ok) {
          const data = (await res.json()) as {
            recipes: Array<{ atUri: string; did: string; handle: string; value: BlueskyRecipeRecord; createdAt: string }>;
            cursor: string | null;
          };
          const converted: FeedRecipe[] = data.recipes.map((r) => ({
            atUri: r.atUri,
            handle: r.handle,
            recipe: blueskyToRecipe(r.value, r.atUri, r.handle),
          }));
          setRecipes(converted);
          setCursor(data.cursor);
          setLoading(false);
          return;
        }
      } catch {
        // fall through
      }

      setServerFeedAvailable(false);
      let handles: string[];
      try {
        const res = await fetch(FEED_API);
        if (res.ok) {
          const data = (await res.json()) as { handle: string }[];
          handles = data.map((d) => d.handle);
        } else {
          handles = SEED_HANDLES;
        }
      } catch {
        handles = SEED_HANDLES;
      }

      const results = await Promise.allSettled(
        handles.map((h) => listBlueskyRecipes(h))
      );

      const all: FeedRecipe[] = [];
      for (const r of results) {
        if (r.status === 'fulfilled') {
          for (const item of r.value.recipes) {
            all.push({ ...item, handle: r.value.handle });
          }
        }
      }

      setRecipes(all);
      setLoading(false);
    })();
  }, []);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`${FEED_RECIPES_API}?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`);
      if (res.ok) {
        const data = (await res.json()) as {
          recipes: Array<{ atUri: string; did: string; handle: string; value: BlueskyRecipeRecord; createdAt: string }>;
          cursor: string | null;
        };
        const converted: FeedRecipe[] = data.recipes.map((r) => ({
          atUri: r.atUri,
          handle: r.handle,
          recipe: blueskyToRecipe(r.value, r.atUri, r.handle),
        }));
        setRecipes((prev) => {
          focusIdxRef.current = prev.length;
          return [...prev, ...converted];
        });
        setCursor(data.cursor);
        setAnnouncement(`Loaded ${converted.length} more recipes.${data.cursor ? '' : ' End of feed.'}`);
      }
    } catch {
      // ignore — button stays enabled for retry
    }
    setLoadingMore(false);
  }

  useEffect(() => {
    if (focusIdxRef.current === null) return;
    const idx = focusIdxRef.current;
    focusIdxRef.current = null;
    requestAnimationFrame(() => {
      const cards = document.querySelectorAll<HTMLElement>('[data-bsky-card]');
      const target = cards[idx];
      if (target) {
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: false });
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, [recipes.length]);

  const categories = new Set<string>();
  const cuisines = new Set<string>();
  for (const { recipe } of recipes) {
    for (const tag of recipe.tags) {
      const lower = tag.toLowerCase();
      if (['dinner', 'breakfast', 'lunch', 'snack', 'dessert', 'side', 'soup', 'entree', 'appetizer'].includes(lower)) categories.add(tag);
      else if (['american', 'italian', 'indian', 'mexican', 'japanese', 'chinese', 'thai', 'french', 'middle_eastern', 'korean', 'mediterranean'].includes(lower)) cuisines.add(tag);
    }
  }

  const filtered = recipes.filter((r) => {
    if (search) {
      const q = search.toLowerCase();
      if (!r.recipe.title.toLowerCase().includes(q) &&
          !r.recipe.tags.some((t) => t.toLowerCase().includes(q)) &&
          !r.recipe.description?.toLowerCase().includes(q)) return false;
    }
    if (activeFilters.size > 0) {
      const recipeTags = new Set(r.recipe.tags.map((t) => t.toLowerCase()));
      for (const f of activeFilters) {
        if (!recipeTags.has(f.toLowerCase())) return false;
      }
    }
    return true;
  });

  function toggleFilter(tag: string) {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      next.has(tag) ? next.delete(tag) : next.add(tag);
      return next;
    });
  }

  function toggleSelect(atUri: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(atUri) ? next.delete(atUri) : next.add(atUri);
      return next;
    });
  }

  async function handleBulkImport() {
    if (selected.size === 0 || importing) return;
    captureActiveElement();
    setImporting(true);
    setImportProgress({ done: 0, total: selected.size });
    let done = 0;
    for (const atUri of selected) {
      const item = recipes.find((r) => r.atUri === atUri);
      if (!item) { done++; continue; }
      try {
        await gql(CREATE_RECIPE, {
          title: item.recipe.title,
          description: item.recipe.description ?? null,
          instructions: item.recipe.instructions,
          servings: item.recipe.servings ?? null,
          prepTime: item.recipe.prepTime ?? null,
          cookTime: item.recipe.cookTime ?? null,
          tags: item.recipe.tags ?? [],
          photoUrl: item.recipe.photoUrl ?? null,
          sourceUrl: item.recipe.sourceUrl,
          ingredients: item.recipe.ingredients,
          kitchenSlug: kitchen,
        });
      } catch (err) {
        console.error('Import failed:', err);
      }
      done++;
      setImportProgress({ done, total: selected.size });
      if (done < selected.size) await new Promise((r) => setTimeout(r, 300));
    }
    setImporting(false);
    setImportProgress(null);
    window.location.href = `${recipesBase}#stage`;
  }

  return (
    <>
      <p className="text-sm text-[var(--color-text-secondary)] mb-4">
        Browsing {loading ? '…' : `${recipes.length} recipes from ${new Set(recipes.map((r) => r.handle)).size} publishers`} on AT Protocol
      </p>

      <div className="mb-4">
        <label htmlFor="bsky-search" className="text-xs font-bold uppercase tracking-wider text-[var(--color-text-secondary)] mb-1 block">Search</label>
        <input
          id="bsky-search"
          type="search"
          list="bsky-suggestions"
          placeholder="mushroom risotto"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="field-input w-full"
        />
        <datalist id="bsky-suggestions">
          {[...categories, ...cuisines].map((tag) => <option key={tag} value={tag} />)}
        </datalist>
      </div>

      {(categories.size > 0 || cuisines.size > 0) && (
        <div className="mb-4 relative">
          <a href="#bsky-after-filters" className="skip-link">Skip filters</a>
          <p id="bsky-filter-label" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)] mb-2">Filter by category</p>
          <div className="flex flex-wrap gap-2" role="group" aria-labelledby="bsky-filter-label">
            {[...categories, ...cuisines].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleFilter(tag)}
                aria-pressed={activeFilters.has(tag)}
                className="text-xs font-medium px-3 py-1.5 rounded-full border-2 transition-colors cursor-pointer"
                style={activeFilters.has(tag)
                  ? { backgroundColor: 'var(--color-accent)', color: 'var(--color-bg-body)', borderColor: 'var(--color-accent)', fontWeight: 700 }
                  : { borderColor: 'var(--color-border-card)', color: 'var(--color-text-secondary)' }
                }
              >
                {tag}
              </button>
            ))}
          </div>
          <div id="bsky-after-filters" tabIndex={-1} className="sr-only">Skipped filters</div>
        </div>
      )}

      {/* Mode toggle */}
      <fieldset className="mb-6 card p-3 text-sm">
        <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">User Flow</legend>
        <div className="flex flex-wrap gap-4 px-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bsky-mode"
              value="browse"
              checked={mode === 'browse'}
              onChange={() => setMode('browse')}
              className="accent-accent"
            />
            <span>Browse &amp; Import</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bsky-mode"
              value="bulk"
              checked={mode === 'bulk'}
              onChange={() => setMode('bulk')}
              className="accent-accent"
            />
            <span>Bulk Import</span>
          </label>
        </div>
      </fieldset>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card rounded-xl h-48 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && (
        <>
          <ImportGrid
            importing={importing}
            importingLabel={importProgress ? `Importing ${importProgress.done}/${importProgress.total}…` : undefined}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4"
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && selected.size > 0) { e.preventDefault(); handleBulkImport(); } }}
            ariaKeyshortcuts="Meta+Enter"
          >
            {filtered.map((item) => {
              const isSelected = selected.has(item.atUri);
              const photo = item.recipe.photoUrl ? (
                <div className="aspect-[16/9] overflow-hidden bg-[var(--color-bg-card)]">
                  <img src={item.recipe.photoUrl} alt={item.recipe.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ) : pixabayActive ? (
                <div className="aspect-[16/9] overflow-hidden bg-[var(--color-bg-card)]">
                  <PixabayImage recipe={{ id: item.atUri, title: item.recipe.title }} apiKey={pixabayKey!} alt={item.recipe.title} inCard />
                </div>
              ) : null;

              const titleBlock = (
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm leading-snug mb-1 line-clamp-2">{item.recipe.title}</h3>
                  <p className="text-xs text-[var(--color-text-secondary)]">@{item.handle}</p>
                </div>
              );

              const tags = item.recipe.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {item.recipe.tags.filter((t) => t !== 'bluesky').slice(0, 4).map((t) => (
                    <span key={t} className="tag text-[10px]">{t}</span>
                  ))}
                </div>
              );

              if (mode === 'browse') {
                const path = `/kitchens/${kitchen}/at/${item.atUri.replace(/^at:\/\//, '')}#stage`;
                return (
                  <a
                    key={item.atUri}
                    href={path}
                    data-bsky-card
                    className="card rounded-xl overflow-hidden flex flex-col transition-colors hover:border-accent"
                  >
                    {photo}
                    <div className="p-4 flex-1 flex flex-col">
                      {titleBlock}
                      {tags}
                    </div>
                  </a>
                );
              }

              return (
                <label key={item.atUri} data-bsky-card className={`card rounded-xl overflow-hidden flex flex-col cursor-pointer transition-colors group ${isSelected ? 'border-accent' : ''}`}>
                  {photo}
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(item.atUri)}
                        className="mt-1 w-4 h-4 shrink-0 accent-accent"
                      />
                      {titleBlock}
                    </div>
                    {tags}
                  </div>
                  {isSelected && selected.size > 0 && (
                    <button type="button" onClick={(e) => { e.preventDefault(); handleBulkImport(); }} className="hidden group-focus-within:block btn-primary text-xs mx-3 mb-3 w-[calc(100%-1.5rem)]">
                      Import {selected.size} selected
                    </button>
                  )}
                </label>
              );
            })}
          </ImportGrid>

          {mode === 'bulk' && selected.size > 0 && (
            <div className="sticky bottom-4 mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleBulkImport}
                disabled={importing}
                className="btn-primary disabled:opacity-50 shadow-lg"
              >
                Import {selected.size} selected
              </button>
            </div>
          )}

          {serverFeedAvailable && cursor && (
            <div className="mt-6 flex justify-center">
              <button
                ref={loadMoreBtnRef}
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                aria-busy={loadingMore}
                className="btn-secondary disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}

          <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>
        </>
      )}

      {!loading && filtered.length === 0 && (
        <p className="text-center text-[var(--color-text-secondary)] py-12">No recipes match your search.</p>
      )}

      <p className="text-center text-xs text-[var(--color-text-secondary)] mt-8">
        Powered by <a href="https://atproto.com" target="_blank" rel="noopener noreferrer" className="underline">AT Protocol</a> · Handle registry at <a href="https://feed.pantryhost.app/api/handles" target="_blank" rel="noopener noreferrer" className="underline">feed.pantryhost.app</a>
      </p>
    </>
  );
}
